import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Check if environment variables are set
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables not found. Using mock implementation.')
  console.log('Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file')
}

// Create Supabase client only if environment variables are available
export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null

// Mock implementation for development without Supabase
const mockSupabase = {
  auth: {
    signUp: async () => ({ data: { user: null }, error: new Error('Supabase not configured') }),
    signInWithPassword: async () => ({ data: { user: null }, error: new Error('Supabase not configured') }),
    signOut: async () => ({ error: new Error('Supabase not configured') }),
    getUser: async () => ({ data: { user: null }, error: new Error('Supabase not configured') }),
    onAuthStateChange: () => ({ data: { subscription: null } })
  },
  from: () => ({
    select: () => ({
      eq: () => ({
        range: () => ({
          order: () => Promise.resolve({ data: [], error: null })
        })
      }),
      single: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') })
    })
  }),
  rpc: () => Promise.resolve({ data: [], error: null })
}

// Export either the real Supabase client or mock implementation
export const db = supabase || mockSupabase

// Auth helper functions
export const signUp = async (email, password, username) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username,
        role: 'user'
      }
    }
  })
  
  return { data, error }
}

export const signIn = async (email, password) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  
  return { data, error }
}

export const signOut = async () => {
  if (!supabase) {
    return { error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = async () => {
  if (!supabase) {
    return { user: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data: { user }, error } = await supabase.auth.getUser()
  return { user, error }
}

export const onAuthStateChange = (callback) => {
  if (!supabase) {
    return { data: { subscription: null } }
  }
  
  return supabase.auth.onAuthStateChange(callback)
}

// Database helper functions
export const createPost = async (postData) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('posts')
    .insert(postData)
    .select()
    .single()
    
  return { data, error }
}

export const getPosts = async (options = {}) => {
  if (!supabase) {
    return { data: [], error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { limit = 20, offset = 0, search = '', sortBy = 'created_at' } = options
  
  let query = supabase
    .from('posts')
    .select(`
      *,
      author:profiles(username, avatar_url),
      comments(count),
      likes(count)
    `)
    .range(offset, offset + limit - 1)
    .order(sortBy, { ascending: false })
    
  if (search) {
    query = query.ilike('title', `%${search}%`)
  }
  
  const { data, error } = await query
  return { data, error }
}

export const createComment = async (commentData) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('comments')
    .insert(commentData)
    .select(`
      *,
      author:profiles(username, avatar_url),
      replies:comments(
        *,
        author:profiles(username, avatar_url)
      )
    `)
    .single()
    
  return { data, error }
}

export const getComments = async (postId, parentCommentId = null) => {
  if (!supabase) {
    return { data: [], error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('comments')
    .select(`
      *,
      author:profiles(username, avatar_url),
      replies:comments(
        *,
        author:profiles(username, avatar_url)
      )
    `)
    .eq('post_id', postId)
    .is('parent_comment_id', parentCommentId)
    .order('created_at', { ascending: true })
    
  return { data, error }
}

export const likePost = async (postId, userId) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('likes')
    .insert({ post_id: postId, user_id: userId })
    
  return { data, error }
}

export const unlikePost = async (postId, userId) => {
  if (!supabase) {
    return { error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { error } = await supabase
    .from('likes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId)
    
  return { error }
}

export const getUserProfile = async (userId) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
    
  return { data, error }
}

export const updateUserProfile = async (userId, profileData) => {
  if (!supabase) {
    return { data: null, error: new Error('Supabase not configured. Please check environment variables.') }
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...profileData })
    .select()
    .single()
    
  return { data, error }
}
